import type { ControlledActionBlockType, ResolvedControlActionWithPayload } from '@lighthouse/core'
import React, { useContext, useMemo } from 'react'

import { getBlockListenerKey } from '../block'
import { useRegisterBlockListeners } from './blockListenerSystem'
import { type CustomViewFrameSource, getViewControlListenerIds, readCustomViewStack } from './controlListenerKey'

type ExtractPayload<CAP, CAT> = CAP extends { blockType: CAT; payload: infer P } ? P : never
type ExtractParams<CP, ActionType> = CP extends { type: ActionType; params: infer Params } ? Params : never

const ViewControlListenerIdsContext = React.createContext<readonly string[]>([])

export const ViewControlListenerIdsProvider = ViewControlListenerIdsContext.Provider

export function useViewControlListenerIds() {
    return useContext(ViewControlListenerIdsContext)
}

export function useViewControlListenerIdList(blockId: string, scope: string | undefined, source?: CustomViewFrameSource) {
    return useMemo(() => getViewControlListenerIds(blockId, scope, readCustomViewStack(source)), [blockId, scope, source])
}

export function useRegisterViewControlListeners<
    BlockType extends ControlledActionBlockType,
    CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>,
    E extends CP['type']
>(
    blockId: string,
    scope: string | undefined,
    blockType: BlockType,
    listenerMap: {
        [K in E]?: (payload?: ExtractParams<CP, K>) => void
    }
) {
    const fromProvider = useViewControlListenerIds()
    const ids = useMemo(
        () => (fromProvider.length > 0 ? fromProvider : [getBlockListenerKey(blockId, scope)]),
        [blockId, fromProvider, scope]
    )
    useRegisterBlockListeners(ids, blockType, listenerMap)
}
